import { Directive, ElementRef, HostBinding, HostListener, Input, AfterViewInit } from '@angular/core';
import { KeyboardNavigationHelper } from './keyboard-navigation.helper';

@Directive({
  selector: '[appRowCheckbox]'
})
export class RowCheckboxDirective implements AfterViewInit {

  @Input() rowIndex: number = 0;
  @Input() tableId: string = '';

  // ARIA label for screen readers
  @HostBinding('attr.aria-label')
  get ariaLabel() {
    return `Select row ${this.rowIndex + 1}`;
  }

  constructor(private readonly el: ElementRef) {}

  ngAfterViewInit() {
    // Add data attributes for DOM queries
    this.el.nativeElement.setAttribute('data-checkbox-row', this.rowIndex.toString());
    this.el.nativeElement.setAttribute('data-table-id', this.tableId);
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    switch (event.key) {
      case 'ArrowRight':
        // Go to first action button or first cell if no actions
        this.navigateToActionOrCell();
        event.preventDefault();
        event.stopPropagation();
        break;
      case 'ArrowUp': {
        // Go to checkbox in previous row
        const prevRow = this.findNextVisibleRow('up');
        if (prevRow !== null) {
          this.navigateToCheckbox(prevRow);
        }
        event.preventDefault();
        event.stopPropagation();
        break;
      }
      case 'ArrowDown': {
        // Go to checkbox in next row
        const nextRow = this.findNextVisibleRow('down');
        if (nextRow !== null) {
          this.navigateToCheckbox(nextRow);
        }
        event.preventDefault();
        event.stopPropagation();
        break;
      }
    }
  }

  // Find the next visible row index in the specified direction
  // Returns null if at boundary or no rows found
  private findNextVisibleRow(direction: 'up' | 'down'): number | string | null {
    return KeyboardNavigationHelper.findNextVisibleRow(this.rowIndex, this.tableId, direction);
  }

  // Navigate to checkbox in target row
  private navigateToCheckbox(row: number | string) {
    const selector = `[data-table-id="${this.tableId}"] [data-checkbox-row="${row}"]`;
    const checkbox = document.querySelector(selector) as HTMLElement;

    if (checkbox) {
      checkbox.focus();
      // For frozen columns, scroll the parent row instead of the element itself
      this.scrollRowIntoView(row);
    }
  }

  // Navigate to first action button or first cell
  private navigateToActionOrCell() {
    // Try to find first action button in same row
    const actionSelector = `[data-table-id="${this.tableId}"] [data-action-row="${this.rowIndex}"][data-action-index="0"]`;
    const actionButton = document.querySelector(actionSelector) as HTMLElement;

    if (actionButton) {
      actionButton.focus();
      // For frozen columns, scroll the parent row instead of the element itself
      this.scrollRowIntoView(this.rowIndex);
    } else {
      // No action buttons, go to first cell
      const cellSelector = `[data-table-id="${this.tableId}"] [data-row-index="${this.rowIndex}"][data-col-index="0"]`;
      const cell = document.querySelector(cellSelector) as HTMLElement;

      if (cell) {
        cell.focus();
        cell.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest'
        });
      }
    }
  }

  // Scroll a row into view (works for frozen columns)
  private scrollRowIntoView(row: number | string) {
    // Find any cell in the target row (use a data cell, not frozen column)
    const cellSelector = `[data-table-id="${this.tableId}"] [data-row-index="${row}"][data-col-index="0"]`;
    const cell = document.querySelector(cellSelector) as HTMLElement;
    
    if (cell) {
      cell.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest'
      });
    }
  }
}
